import styled, { createGlobalStyle, css, keyframes } from "styled-components";

/* ================= Fonts ================= */
export const FontImport = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;800;900&family=Sora:wght@300;400;500;600;700&display=swap');
`;

/* ================= Animations ================= */
const auroraMove = keyframes`
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(5%, -6%, 0) scale(1.15); }
  100% { transform: translate3d(-4%, 4%, 0) scale(0.95); }
`;

const gridPan = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 0 -64px; }
`;

const floatUp = keyframes`
  0%   { transform: translateY(0) scale(0.6); opacity: 0; }
  10%  { opacity: .9; }
  85%  { opacity: .35; }
  100% { transform: translateY(-105vh) scale(1.4); opacity: 0; }
`;

const gradientFlow = keyframes`
  to { background-position: 200% center; }
`;

const shimmer = keyframes`
  100% { transform: translateX(220%); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 6px currentColor; }
  50%      { opacity: .5; box-shadow: 0 0 16px currentColor; }
`;

const glowBox = keyframes`
  0%, 100% { box-shadow: 0 0 25px rgba(34, 211, 238, 0.45); }
  50%      { box-shadow: 0 0 48px rgba(34, 211, 238, 0.85); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-14px); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const pulseScale = keyframes`
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50%      { transform: translate(-50%, -50%) scale(1.06); }
`;

const loadGlow = keyframes`
  0%   { box-shadow: 0 0 4px rgba(34, 211, 238, 0.3); }
  50%  { box-shadow: 0 0 26px rgba(34, 211, 238, 0.95); }
  100% { box-shadow: 0 0 14px rgba(34, 211, 238, 0.5); }
`;

const bootFlicker = keyframes`
  0%   { opacity: 0; filter: brightness(2.6); }
  8%   { opacity: 1; }
  10%  { opacity: 0.2; }
  18%  { opacity: 1; }
  22%  { opacity: 0.35; }
  30%  { opacity: 1; filter: brightness(1.8); }
  100% { opacity: 1; filter: brightness(1); }
`;

/* ================= Base ================= */
export const PageWrapper = styled.div`
  --bg: #05060f;
  --bg-soft: #0b0f2a;
  --glass: rgba(255, 255, 255, 0.04);
  --border: rgba(148, 163, 184, 0.14);
  --cyan: #22d3ee;
  --blue: #3b82f6;
  --violet: #8b5cf6;
  --pink: #f472b6;
  --text: #eaf2ff;
  --muted: #93a4bd;

  position: relative;
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: "Sora", "Segoe UI", system-ui, -apple-system, sans-serif;
  overflow-x: hidden;
  scroll-behavior: smooth;

  ::selection {
    background: rgba(34, 211, 238, 0.35);
  }
`;

export const Container = styled.div`
  position: relative;
  z-index: 2;
  max-width: min(1600px, 92vw);
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 4rem) 1.25rem 5rem;
`;

/* ================= Background FX ================= */
export const Background = styled.div`
  position: fixed;
  inset: 0;
  z-index: -10;
  overflow: hidden;
  background: radial-gradient(
    ellipse 80% 60% at 50% -10%,
    #131a3d 0%,
    var(--bg) 55%
  );
`;

export const Aurora = styled.div`
  position: absolute;
  inset: -10%;
  filter: blur(90px);
  opacity: 0.55;
`;

export const AuroraBlob = styled.span<{
  $color: string;
  $size: string;
  $top: string;
  $left: string;
  $duration: number;
  $delay: number;
}>`
  position: absolute;
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  background: ${({ $color }) => $color};
  border-radius: 50%;
  mix-blend-mode: screen;
  animation: ${auroraMove} ${({ $duration }) => $duration}s ease-in-out
    infinite alternate;
  animation-delay: ${({ $delay }) => $delay}s;
`;

export const GridOverlay = styled.div`
  position: absolute;
  inset: -1px;
  background-image: linear-gradient(
      rgba(34, 211, 238, 0.07) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(34, 211, 238, 0.07) 1px, transparent 1px);
  background-size: 64px 64px;
  -webkit-mask: radial-gradient(
    ellipse 70% 50% at 50% 0%,
    #000 0%,
    transparent 75%
  );
  mask: radial-gradient(ellipse 70% 50% at 50% 0%, #000 0%, transparent 75%);
  animation: ${gridPan} 9s linear infinite;
`;

export const Particles = styled.div`
  position: absolute;
  inset: 0;
`;

export const Particle = styled.span<{
  $left: number;
  $size: number;
  $duration: number;
  $delay: number;
  $color: string;
}>`
  position: absolute;
  bottom: -5%;
  left: ${({ $left }) => $left}%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 ${({ $size }) => $size * 2}px ${({ $color }) => $color};
  animation: ${floatUp} ${({ $duration }) => $duration}s linear infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`;

export const Spotlight = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  mix-blend-mode: screen;
  background: radial-gradient(
    620px circle at var(--x, 50%) var(--y, 40%),
    rgba(34, 211, 238, 0.08),
    transparent 70%
  );
`;

/* ================= Header ================= */
export const Header = styled.header`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;
  text-align: center;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    text-align: left;
  }
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  @media (min-width: 768px) {
    align-items: flex-start;
  }
`;

export const Title = styled.h1`
  font-family: "Orbitron", ui-sans-serif, system-ui;
  font-weight: 800;
  font-size: clamp(2.6rem, 5.5vw, 4.75rem);
  line-height: 1.05;
  margin: 0;
  letter-spacing: 0.02em;
  background: linear-gradient(
    90deg,
    var(--cyan),
    var(--blue),
    var(--violet),
    var(--cyan)
  );
  background-size: 250% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ${gradientFlow} 7s linear infinite, ${bootFlicker} 1.4s steps(14) 1;
`;

export const Underline = styled.div`
  position: relative;
  height: 4px;
  width: 8rem;
  border-radius: 4px;
  overflow: hidden;
  background: linear-gradient(90deg, var(--cyan), var(--violet));

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.9),
      transparent
    );
    transform: translateX(-150%);
    animation: ${shimmer} 2.4s ease-in-out infinite;
  }
`;

export const SubTitle = styled.p`
  margin: 0;
  color: #9fe7ff;
  font-weight: 300;
  font-size: clamp(1.2rem, 1.6vw, 1.625rem);
  letter-spacing: 0.02em;
`;

/* ================= Hero badge ================= */
export const HeroBadge = styled.div`
  position: relative;
  width: clamp(10rem, 14vw, 12.5rem);
  height: clamp(10rem, 14vw, 12.5rem);
  animation: ${floatY} 6s ease-in-out infinite;
`;

export const OrbitRing = styled.div`
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    var(--cyan),
    var(--violet),
    var(--blue),
    var(--cyan)
  );
  animation: ${spin} 9s linear infinite;
  -webkit-mask: radial-gradient(
    farthest-side,
    transparent calc(100% - 4px),
    #000 calc(100% - 3px)
  );
  mask: radial-gradient(
    farthest-side,
    transparent calc(100% - 4px),
    #000 calc(100% - 3px)
  );
  filter: drop-shadow(0 0 12px rgba(34, 211, 238, 0.45));
`;

export const Hexagon = styled.div`
  position: absolute;
  inset: 10px;
  background: linear-gradient(145deg, #1a2a6c, #22d3ee);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  display: grid;
  place-items: center;
  animation: ${glowBox} 3s ease-in-out infinite;
`;

export const HexagonInner = styled.div`
  width: 86%;
  height: 86%;
  background: var(--bg-soft);
  clip-path: inherit;
  display: grid;
  place-items: center;
`;

export const Availability = styled.div`
  position: absolute;
  left: 50%;
  bottom: -1.3rem;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  background: rgba(10, 14, 32, 0.85);
  border: 1px solid rgba(34, 211, 238, 0.4);
  backdrop-filter: blur(8px);
  color: #aef1ff;
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.18em;
  padding: 0.4rem 1.1rem;
  border-radius: 9999px;
  box-shadow: 0 8px 24px -10px rgba(34, 211, 238, 0.5);

  &::before {
    content: "";
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #34ffa0;
    color: #34ffa0;
    animation: ${glowPulse} 1.6s ease-in-out infinite;
  }
`;

/* ================= Layout ================= */
export const Grid = styled.main`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-top: 3rem;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
    align-items: start;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

/* ================= Scroll reveal ================= */
export const Reveal = styled.div<{ $show: boolean; $delay?: number }>`
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transform: translateY(${({ $show }) => ($show ? "0px" : "36px")});
  transition: opacity 5s cubic-bezier(0.16, 1, 0.3, 1)
      ${({ $delay = 0 }) => $delay}s,
    transform 5s cubic-bezier(0.16, 1, 0.3, 1) ${({ $delay = 0 }) => $delay}s;
  will-change: transform, opacity;
`;

/* ================= Glass cards ================= */
export const GlassCard = styled.section<{ $accent?: boolean }>`
  position: relative;
  border-radius: 24px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(34, 211, 238, 0.5),
    rgba(139, 92, 246, 0.35),
    rgba(59, 130, 246, 0.5),
    rgba(34, 211, 238, 0.5)
  );
  background-size: 300% 300%;
  animation: ${gradientFlow} 14s linear infinite;
  transform: perspective(1000px) rotateX(var(--rx, 0deg))
    rotateY(var(--ry, 0deg));
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.5s ease;

  &:hover {
    box-shadow: 0 25px 60px -25px rgba(34, 211, 238, 0.35);
  }

  ${({ $accent }) =>
    $accent &&
    css`
      box-shadow: 0 0 0 1px rgba(34, 211, 238, 0.25),
        0 25px 60px -30px rgba(34, 211, 238, 0.45);
    `}
`;

export const GlassCardSurface = styled.div`
  position: relative;
  height: 100%;
  background: linear-gradient(
    160deg,
    rgba(17, 24, 47, 0.85),
    rgba(10, 14, 32, 0.78)
  );
  backdrop-filter: blur(18px);
  border-radius: 23px;
  padding: clamp(1.25rem, 2vw, 2rem);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      280px circle at var(--mx, 50%) var(--my, 0%),
      rgba(34, 211, 238, 0.12),
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  ${GlassCard}:hover &::before {
    opacity: 1;
  }
`;

export const HudCorner = styled.span<{ $pos: "tl" | "tr" | "bl" | "br" }>`
  position: absolute;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(34, 211, 238, 0.55);
  opacity: 0.85;
  pointer-events: none;
  z-index: 3;
  filter: drop-shadow(0 0 4px rgba(34, 211, 238, 0.5));

  ${({ $pos }) => {
    switch ($pos) {
      case "tl":
        return css`
          top: 10px;
          left: 10px;
          border-right: none;
          border-bottom: none;
          border-top-left-radius: 4px;
        `;
      case "tr":
        return css`
          top: 10px;
          right: 10px;
          border-left: none;
          border-bottom: none;
          border-top-right-radius: 4px;
        `;
      case "bl":
        return css`
          bottom: 10px;
          left: 10px;
          border-right: none;
          border-top: none;
          border-bottom-left-radius: 4px;
        `;
      case "br":
      default:
        return css`
          bottom: 10px;
          right: 10px;
          border-left: none;
          border-top: none;
          border-bottom-right-radius: 4px;
        `;
    }
  }}
`;

export const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  font-family: "Orbitron", ui-sans-serif, system-ui;
  font-weight: 700;
  font-size: clamp(1.5rem, 2vw, 2.1rem);
  letter-spacing: 0.05em;
  color: #eef6ff;
  margin: 0 0 1.5rem 0;

  svg {
    flex-shrink: 0;
    color: var(--cyan);
    filter: drop-shadow(0 0 8px rgba(34, 211, 238, 0.55));
  }
`;

/* ================= Tech / Skills ================= */
export const TechGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.1rem;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const TechCard = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 1.25rem;
  overflow: hidden;
  transform: perspective(800px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    border-color 0.3s ease, box-shadow 0.3s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      220px circle at var(--mx, 50%) var(--my, 0%),
      rgba(34, 211, 238, 0.15),
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  &:hover {
    border-color: rgba(34, 211, 238, 0.5);
    box-shadow: 0 18px 40px -22px rgba(34, 211, 238, 0.4);
  }

  &:hover::before {
    opacity: 1;
  }
`;

export const TechCardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #bfe4ff;
  font-weight: 700;
  font-size: 1.3rem;
  margin: 0 0 1rem 0;
  letter-spacing: 0.02em;

  svg {
    color: var(--cyan);
  }
`;

export const SkillRow = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SkillHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.4rem;
  font-size: 1.25rem;
  color: #dbeafe;
`;

export const Meter = styled.div`
  position: relative;
  width: 100%;
  height: 11px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.18);
`;

export const SkillBar = styled.div<{ $level: number; $show: boolean }>`
  position: relative;
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--cyan), var(--blue), var(--violet));
  width: ${({ $show, $level }) => ($show ? `${$level}%` : "0%")};
  transition: width 5s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 0 14px rgba(34, 211, 238, 0.5);
  overflow: hidden;
  animation: ${({ $show }) => ($show ? css`${loadGlow} 5s ease-out` : "none")};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
      90deg,
      transparent 0,
      transparent calc(10% - 3px),
      rgba(5, 6, 15, 0.5) calc(10% - 3px),
      rgba(5, 6, 15, 0.5) 10%
    );
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    width: 40%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.65),
      transparent
    );
    transform: translateX(-150%);
    animation: ${shimmer} 2.4s ease-in-out infinite;
  }
`;

export const LevelTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-family: "Orbitron", ui-sans-serif, system-ui;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #bff3ff;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  background: rgba(34, 211, 238, 0.1);
  border: 1px solid rgba(34, 211, 238, 0.35);

  &::before {
    content: "LV";
    color: var(--cyan);
    font-size: 0.9rem;
    letter-spacing: 0.05em;
  }
`;

export const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.95rem;
  border-radius: 9999px;
  font-size: 1.2rem;
  color: #a8e9ff;
  background: rgba(34, 211, 238, 0.07);
  border: 1px solid rgba(34, 211, 238, 0.25);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(34, 211, 238, 0.15);
    border-color: rgba(34, 211, 238, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px -10px rgba(34, 211, 238, 0.5);
  }
`;

/* ================= Timeline (experiência / educação) ================= */
export const Timeline = styled.div`
  position: relative;
  display: grid;
  gap: 2rem;
  padding-left: 1.85rem;

  &::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 0.4rem;
    bottom: 0.4rem;
    width: 2px;
    background: linear-gradient(180deg, var(--cyan), rgba(139, 92, 246, 0.15));
  }
`;

export const TimelineItem = styled.div`
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: -1.85rem;
    top: 0.35rem;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--bg-soft);
    border: 2px solid var(--cyan);
    box-shadow: 0 0 12px rgba(34, 211, 238, 0.6);
  }
`;

export const TimelineRole = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #eef6ff;
`;

export const TimelineMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.8rem;
  margin-top: 0.3rem;
  color: var(--muted);
  font-size: 1.2rem;
`;

export const TimelineBody = styled.div`
  margin-top: 0.6rem;
  color: #b9c6dd;
  font-size: 1.3rem;
  line-height: 1.6;

  ul {
    margin: 0.5rem 0 0 1.1rem;
    padding: 0;
  }

  li {
    margin-bottom: 0.25rem;
  }
`;

export const StackRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.7rem;
`;

export const StackTag = styled.span`
  font-size: 1.05rem;
  padding: 0.25rem 0.7rem;
  border-radius: 9999px;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.3);
`;

/* ================= Projetos ================= */
export const ProjectGrid = styled.div`
  display: grid;
  gap: 1.25rem;

  @media (min-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const ProjectCard = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 1.4rem;
  transform: perspective(800px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    border-color 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    border-color: rgba(139, 92, 246, 0.5);
    box-shadow: 0 18px 40px -22px rgba(139, 92, 246, 0.4);
  }
`;

export const ProjectHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const ProjectTitle = styled.h3`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #eef6ff;
`;

export const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.85rem;
  color: var(--cyan);
  font-size: 1.15rem;
  font-weight: 600;
  text-decoration: none;
  transition: gap 0.25s ease, color 0.25s ease;

  &:hover {
    color: #fff;
    gap: 0.7rem;
  }
`;

/* ================= Certificações ================= */
export const CertGrid = styled.div`
  display: grid;
  gap: 0.9rem;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const CertCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 0.9rem 1.1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 215, 106, 0.55);
    box-shadow: 0 12px 30px -18px rgba(255, 215, 106, 0.45);
    transform: translateX(4px);
  }
`;

export const CertIcon = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 2.6rem;
  height: 2.6rem;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    rgba(255, 215, 106, 0.22),
    rgba(255, 140, 0, 0.16)
  );
  border: 1px solid rgba(255, 215, 106, 0.35);
  color: #ffd76a;
  filter: drop-shadow(0 0 6px rgba(255, 215, 106, 0.45));
`;

export const AchievementRibbon = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-family: "Orbitron", ui-sans-serif, system-ui;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #ffd76a;
  text-transform: uppercase;
  opacity: 0.85;
`;

export const CertText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;

  strong {
    font-size: 1.2rem;
    color: #e7eef7;
  }
`;

/* ================= Contato ================= */
export const ContactList = styled.div`
  display: grid;
  gap: 0.9rem;
`;

export const ContactLink = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border);
  color: #dbeafe;
  text-decoration: none;
  font-size: 1.25rem;
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);

  span {
    word-break: break-all;
  }

  &:hover {
    color: #fff;
    border-color: rgba(34, 211, 238, 0.55);
    transform: translateX(6px);
    box-shadow: 0 12px 30px -16px rgba(34, 211, 238, 0.5);
  }
`;

export const ContactIcon = styled.div`
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  display: grid;
  place-items: center;
  border-radius: 9999px;
  background: rgba(34, 211, 238, 0.08);
  border: 1px solid rgba(34, 211, 238, 0.35);
  color: var(--cyan);
  transition: all 0.35s ease;

  ${ContactLink}:hover & {
    background: var(--cyan);
    color: #04101c;
    box-shadow: 0 0 18px rgba(34, 211, 238, 0.7);
  }
`;

/* ================= Orbit (Tech Stack) ================= */
export const OrbitArea = styled.div`
  position: relative;
  height: 380px;
  border-radius: 18px;
  overflow: hidden;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(34, 211, 238, 0.06),
    transparent 65%
  );
`;

export const CoreNode = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 92px;
  height: 92px;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, var(--blue), var(--cyan));
  border: 2px solid rgba(34, 211, 238, 0.6);
  display: grid;
  place-items: center;
  border-radius: 9999px;
  animation: ${pulseScale} 2.6s ease-in-out infinite;
  box-shadow: 0 0 0 10px rgba(34, 211, 238, 0.08),
    0 0 40px rgba(34, 211, 238, 0.35);
  z-index: 2;
`;

export const Bubble = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(148, 163, 184, 0.25);
  display: grid;
  place-items: center;
  color: #cbd5f5;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  transition: border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease,
    z-index 0.3s ease;
  will-change: transform;
  cursor: default;

  &:hover {
    border-color: rgba(34, 211, 238, 0.85);
    color: var(--cyan);
    box-shadow: 0 0 24px rgba(34, 211, 238, 0.5);
    z-index: 5;
  }
`;

/* ================= Texto utilitário ================= */
export const Muted = styled.p`
  color: var(--muted);
  margin: 0;
  line-height: 1.7;
`;

export const Small = styled.span`
  color: var(--muted);
  font-size: 1.2rem;
`;
